import { Box, Typography } from '@mui/material';
import Cookies from 'js-cookie';
import { useEffect, useMemo, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import FlagIcon from '../../../../components/Icons/FlagIcon';
import BaseModal from '../../../../components/MUIComponents/BaseModal';
import Button from '../../../../components/MUIComponents/Button';
import CustomButton from '../../../../components/MUIComponents/CustomButton';
import CustomStepper from '../../../../components/MUIComponents/CustomStepper';
import Loader from '../../../../components/MUIComponents/Loader';
import SnackbarCustom from '../../../../components/MUIComponents/SnackbarCustom';
import { Loading } from '../../../../components/shared/loader/Loader';
import { ButtonTypeEnum } from '../../../../enums/button-type-enum';
import { CookieEnum } from '../../../../enums/cookie-enum';
import { SnackbarSeverityEnum } from '../../../../enums/snackbar-severity-enum';
import { useInviteTokenValidation } from '../../../../hooks/company/registration/useInviteTokenValidation';
import { useModalManager } from '../../../../hooks/useModalManager';
import useSnackbar from '../../../../hooks/useSnackBar';
import { IUserFileDB } from '../../../../interfaces/file.interface';
import {
  useCreateCompanyMutation,
  useGetMyCompanyQuery,
} from '../../../../store/apiSlice/companyApi';
import { useUploadDocumentsMutation } from '../../../../store/apiSlice/userApi';
import { errorHelper } from '../../../../utils/helper/error-helper';
import AccountSetupForm from './AccountSetupForm';
import CompanyStepsNeededAuth from './CompanyStepsNeededAuth';

export interface CompanyDetailsFormInputs {
  companyName: string;
  industryIds: string[];
  address: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  phone: string;
  contactEmail: string;
  website?: string;
}

interface CompanyFormsData extends CompanyDetailsFormInputs {
  documents: File[];
}

interface FileUploadFormInputs {
  documents: File[];
}

const CompanyRegistration = () => {
  const navigate = useNavigate();
  const { currentModal, toggleModal } = useModalManager();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<IUserFileDB[]>([]);
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const [isCompanyInfoExists, setIsCompanyInfoExists] =
    useState<boolean>(false);
  const { isInviteTokenLoading } = useInviteTokenValidation({
    showError: showSnackbar,
  });

  const [
    createCompanyInfo,
    {
      isLoading: _createCompanyInfoIsLoading,
      isSuccess: _createCompanyInfoIsSuccess,
      data: _createdData,
    },
  ] = useCreateCompanyMutation();

  const [
    uploadDocuments,
    {
      isLoading: idConfirmationFilesIsLoading,
      isError: idConfirmationFilesIsError,
      error: idConfirmationFilesError,
      data: idConfirmationFilesResponseData,
    },
  ] = useUploadDocumentsMutation();

  const {
    data: myCompanyData,
    isLoading: getMyCompanyInfoIsLoading,
    isSuccess: getMyCompanyInfoIsSuccess,
  } = useGetMyCompanyQuery();

  useEffect(() => {
    if (
      Cookies.get(CookieEnum.ACCESS_TOKEN) &&
      getMyCompanyInfoIsSuccess &&
      myCompanyData
    ) {
      showSnackbar(
        'You have already verified company info.\nYou can upload documents to verify your company if needed.',
        SnackbarSeverityEnum.SUCCESS
      );
      setIsCompanyInfoExists(true);
      setStep(3);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMyCompanyInfoIsSuccess, myCompanyData]); // Intentionally omitting showSnackbar - custom hook function not memoized

  const onSubmitAccountSetup = (): void => {
    setStep(step + 1);
  };

  const onSubmitCompanyDetails: SubmitHandler<CompanyDetailsFormInputs> = (
    data
  ) => {
    setFormData({ ...formData, ...data });
    setStep(step + 1);
  };

  const onSubmitFiles: SubmitHandler<FileUploadFormInputs> = async (data) => {
    try {
      const finalData = { ...formData, ...data } as CompanyFormsData;

      if (!isCompanyInfoExists) {
        const companyInfoData = {
          companyName: finalData.companyName,
          industryIds: finalData.industryIds,
          address: finalData.address,
          city: finalData.city,
          state: finalData.state,
          country: finalData.country,
          zipCode: finalData.zipCode,
          phone: finalData.phone,
          contactEmail: finalData.contactEmail,
          website: finalData?.website,
        };

        const companyInfoResponse = await createCompanyInfo(companyInfoData);
        if (Object.hasOwn(companyInfoResponse, 'error')) {
          throw companyInfoResponse.error;
        }
      }

      if (finalData.documents.length > 0) {
        const formData = new FormData();
        finalData.documents.forEach((file) => {
          formData.append('documents', file);
        });
        const filesResponse = await uploadDocuments(formData);
        if (Object.hasOwn(filesResponse, 'error')) {
          throw filesResponse.error;
        }
      }
      showSnackbar(
        'Company registration successful',
        SnackbarSeverityEnum.SUCCESS
      );
      setFiles([]);
      setUploadedFiles([]);
      toggleModal('WELCOME_MODAL');
    } catch (error) {
      const errorMessage = errorHelper(error);
      showSnackbar(errorMessage, SnackbarSeverityEnum.ERROR);
    }
  };

  const steps = useMemo(
    () => ['Account setup', 'Company details', 'Upload documents'],
    []
  );

  const title = useMemo(
    () => (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
      >
        <Box m="24px 0 36px">
          <FlagIcon />
        </Box>
        <Typography variant="h2">Welcome to Free Agent!</Typography>
      </Box>
    ),
    []
  );

  const footer = useMemo(
    () => (
      <Box
        display="flex"
        flexDirection={'row'}
        gap={'22px'}
        justifyContent="center"
        m="36px 0 36px"
      >
        <Button
          onClick={() => navigate('/company-questionnaire', { replace: true })}
          variant="contained"
          width={220}
          height={50}
        >
          start now
        </Button>
        <CustomButton
          onClick={() => navigate('/subscription-plans', { replace: true })}
          variantType={ButtonTypeEnum.SECONDARY}
          buttonWidth={'220px'}
        >
          i’ll do it later
        </CustomButton>
      </Box>
    ),
    [navigate]
  );

  const handleCloseModal = () => {
    toggleModal('');
    navigate('/subscription-plans', { replace: true });
  };

  if (isInviteTokenLoading) {
    return <Loading />;
  }

  return (
    <>
      {currentModal === 'WELCOME_MODAL' && (
        <BaseModal
          boxSx={{ width: '875px', maxHeight: 756, overflowY: 'scroll' }}
          header={{
            component: title,
          }}
          isOpen={true}
          toggle={() => handleCloseModal()}
          footer={{ component: footer }}
        >
          <Typography variant="body2" textAlign="center">
            To tailor the best experience for you, please answer a few quick
            questions. Your responses will help us recommend the perfect
            subscription plan to meet your hiring needs.
          </Typography>
        </BaseModal>
      )}

      <Box
        display={'flex'}
        maxWidth={'875px'}
        margin={'0 auto'}
        flexDirection={'column'}
        justifyContent={'center'}
        alignItems={'center'}
        marginBottom={'64px'}
      >
        <CustomStepper steps={steps} step={step} />

        {getMyCompanyInfoIsLoading ? (
          <Loader size={50} color={'primary'} />
        ) : (
          <>
            {step === 1 && (
              <AccountSetupForm
                onSubmitAccountSetup={onSubmitAccountSetup}
                setStep={setStep}
                step={step}
              />
            )}
            <CompanyStepsNeededAuth
              step={step}
              onSubmitCompanyDetails={onSubmitCompanyDetails}
              formData={formData as CompanyDetailsFormInputs}
              setStep={setStep}
              onSubmitFiles={onSubmitFiles}
              formDataFiles={formData as FileUploadFormInputs}
              files={files}
              setFiles={setFiles}
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
              idConfirmationFilesIsLoading={idConfirmationFilesIsLoading}
              idConfirmationFilesIsError={idConfirmationFilesIsError}
              idConfirmationFilesError={idConfirmationFilesError}
              idConfirmationFilesResponseData={idConfirmationFilesResponseData}
              isCompanyInfoExists={isCompanyInfoExists}
            />

            <SnackbarCustom
              open={snackbar.open}
              onClose={closeSnackbar}
              message={snackbar.message}
              severity={snackbar.severity}
              autoHideDuration={null}
            />
          </>
        )}
      </Box>
    </>
  );
};

export default CompanyRegistration;
